import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import {
  EatAndDrinkBookFlowHowManyPropertiesModel,
  EatAndDrinkBookFlowWhenPropertiesModel,
  EventBookFlowFormItemPropertiesModel,
  EventBookFlowHowManyElementModelContentType,
  EventBookFlowWhenElementModelContentType,
  EventBookFlowWhereElementModelContentType,
  EventBookFlowWherePropertiesModel,
  EventBookFlowWhichElementModelContentType,
  EventBookFlowWhichPropertiesModel,
} from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ProgressDots } from '@/components/shared/progressDots';
import { FormRow } from '@/components/shared/formRow';
import { HowManySheet } from '../eat-and-drink/sheets/howMany';
import { WhenSheet } from '../eat-and-drink/sheets/when';
import { WhichSheet } from './sheets/which';
import { EventWhereSheet } from './sheets/where';
import { DateUnavailableSheet } from './sheets/dateUnavailable';
import { formatDate, formatTime } from '@/lib/core/utils';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { isRegistrationClosed } from '@/components/shared/events/list/format';
import {
  isOpenForRegistration,
  isSoldOut,
  resolveBookingScope,
  resolveGuestLimit,
  resolvePaymentDecision,
} from '@/components/shared/events/rules';
import { useEventGroupEvents } from '@/services/platform/data/events';
import {
  buildWhereOptions,
  resolveEnabledDates,
  resolveOccurrenceTime,
  resolveWhenMode,
  type WhenMode,
} from './derive';
import type { OpenSheet, EventBookProps } from './types';

// ── EventBook ────────────────────────────────────────────────────────────────

export function EventBook({
  cmsProps,
  booking,
  onBookingChange,
  onBack,
  onContinue,
  onRequestSuccess,
  step,
  unavailableDate,
}: EventBookProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);
  // Shown once per unbookable day that comes in — it arrives with the preselect fetch, after mount;
  // dismissing it leaves "When" open to choose.
  const [isUnavailableNoticeOpen, setIsUnavailableNoticeOpen] = useState(false);
  useEffect(() => {
    if (unavailableDate) setIsUnavailableNoticeOpen(true);
  }, [unavailableDate]);
  // The member picks the day themselves — never the one remaining date pre-filled for them — and
  // "When" stays editable after they do, rather than locking onto their pick as a fixed date.
  const mustChooseDate = !!unavailableDate;

  const formItems = (cmsProps?.bookForm?.items ?? []).map(
    item => item.content?.properties as EventBookFlowFormItemPropertiesModel | undefined
  );

  const titleHtml = cmsProps?.title?.markup;
  const continueLabel = cmsProps?.continueLabel ?? t('App.Labels.Continue');

  // "Where" options: this event's own occurrence venues plus any sibling editions of the
  // same group (contract v1.12 §3.0) — needs the user's own pick before "When" can be scoped
  // to a venue's dates, so `venueKey` is undefined until then.
  const { events: groupEvents, isLoading: isLoadingGroupEvents } = useEventGroupEvents(
    booking.event?.groupId,
    {
      excludeEventId: booking.event?.id,
    }
  );
  const whereOptions = booking.event ? buildWhereOptions(booking.event, groupEvents) : [];
  const hasWhereChoice = whereOptions.length > 1;
  const venueKey = booking.venue?.venueNodeKey ?? booking.venue?.name ?? undefined;

  // `whenMode` decides how the "When" field behaves: a single instance is pre-filled
  // and locked, several dated instances with no time-of-day only ask for a day, and
  // several dated instances with a time-of-day get the normal SevenRooms date+time picker —
  // but only when the reservation actually lives in SevenRooms. A `Salesforce` (or any other
  // non-SevenRooms) event has no rooms/availability to pick from: its occurrence's time is
  // already fixed, so it behaves like `dateOnly` even though it isn't all-day.
  const whenMode: WhenMode = booking.event ? resolveWhenMode(booking.event, venueKey) : 'locked';
  const usesRoomTimePicker = whenMode === 'normal' && booking.event?.bookingMode === 'SevenRooms';
  // A day that came in unbookable opens "When" even when a single date is left (`locked`): that
  // date is offered, not filled in, so the member sees their day did not stand.
  const offersDatePicker = whenMode !== 'locked' || mustChooseDate;
  const enabledDates =
    booking.event && offersDatePicker ? resolveEnabledDates(booking.event, venueKey) : undefined;

  // Gating reads the chosen occurrence first, the event only as fallback (contract §3.4:
  // validations are per occurrence) — `booking.occurrence` is kept in sync by `applyEventBookingPatch`.
  const scope = booking.event ? resolveBookingScope(booking.event, booking.occurrence) : null;
  const guestLimit = scope ? resolveGuestLimit(scope) : undefined;

  const whichDisplay = booking.event?.title ?? '';
  const whereDisplay = booking.venue?.name ?? '';
  const whereIsEmpty = !whereDisplay;
  const guestDisplay = booking.guestCount !== null ? `${booking.guestCount}` : '';
  // The room/time picker's own selection when it applies; otherwise the resolved occurrence's
  // fixed time (never a user choice — see `usesRoomTimePicker`).
  const displayTime = usesRoomTimePicker ? booking.time : resolveOccurrenceTime(booking.occurrence);
  const whenDisplay = booking.date
    ? displayTime
      ? `${formatDate(booking.date.toISOString())}, ${formatTime(displayTime)}`
      : formatDate(booking.date.toISOString())
    : '';

  // Every visible field satisfied — event selection unlocks How many/When (see
  // `isFieldDisabled`); Where is derived from the event unless it spans multiple venues.
  const rowIsSet = formItems.map(formItem => {
    const stepType = formItem?.stepPicker?.items?.[0]?.content?.contentType;
    switch (stepType) {
      case EventBookFlowWhichElementModelContentType.eventBookFlowWhich:
        return !!booking.event;
      case EventBookFlowWhereElementModelContentType.eventBookFlowWhere:
        return !whereIsEmpty;
      case EventBookFlowHowManyElementModelContentType.eventBookFlowHowMany:
        return booking.guestCount !== null;
      case EventBookFlowWhenElementModelContentType.eventBookFlowWhen:
        return booking.date !== null && (!usesRoomTimePicker || booking.time !== null);
      default:
        return false;
    }
  });
  // Registration deadline, sold-out and status close booking regardless of the form state —
  // all mirror server-side validations (contract §3.4) so the FE doesn't cause an avoidable
  // 409/422 by submitting a request the occurrence itself already can't accept.
  const registrationClosed = scope ? isRegistrationClosed(scope.registrationDeadline) : false;
  const soldOut = scope ? isSoldOut(scope) : false;
  const notOpenForRegistration = scope ? !isOpenForRegistration(scope) : false;
  const notForSale = scope ? resolvePaymentDecision(scope) === 'notForSale' : false;
  const overGuestLimit = !!guestLimit && (booking.guestCount ?? 0) > guestLimit;
  const canContinue =
    rowIsSet.length > 0 &&
    rowIsSet.every(Boolean) &&
    !registrationClosed &&
    !soldOut &&
    !notOpenForRegistration &&
    !notForSale &&
    !overGuestLimit;

  // Unlike eat-and-drink's linear Where→HowMany→When cascade, the event form has fields
  // with a mostly-fixed disabled state: Which is always open (it's what unlocks everything
  // else); Where stays locked (derived from the event) unless there's a real choice — another
  // occurrence venue or a sibling group edition; How many is locked too once the event's guest
  // limit leaves no real choice (cap of 1, auto-filled — see `deriveBookingUpdatesFromEvent`);
  // When waits on Where when there's a real venue choice to make, AND on How many always — a
  // preselected `booking.date` (e.g. arriving from the Events Calendar with a day already
  // tapped) must not let the user open When before guests are set, same as if they'd picked
  // the day themselves.
  const isFieldDisabled = (stepType?: string) => {
    switch (stepType) {
      case EventBookFlowWhichElementModelContentType.eventBookFlowWhich:
        return false;
      case EventBookFlowWhereElementModelContentType.eventBookFlowWhere:
        return !booking.event || !hasWhereChoice;
      case EventBookFlowWhenElementModelContentType.eventBookFlowWhen:
        return (
          !booking.event ||
          !offersDatePicker ||
          (hasWhereChoice && !booking.venue) ||
          booking.guestCount === null
        );
      case EventBookFlowHowManyElementModelContentType.eventBookFlowHowMany:
        return !booking.event || guestLimit === 1;
      default:
        return true;
    }
  };

  // What "When" still waits on, in form order — the same prerequisites as its row above. "Choose
  // another date" goes through here rather than straight to "When": opened without guests, the
  // SevenRooms availability call is rejected (`guestCount must be an integer >= 1`).
  const whenPrerequisite: OpenSheet =
    hasWhereChoice && !booking.venue ? 'where' : booking.guestCount === null ? 'howmany' : null;
  // The prerequisite sheet opened on the way to "When" — once it closes answered, the next one (or
  // "When" itself) follows; closed unanswered, the member is left on the form.
  const [resumeWhenVia, setResumeWhenVia] = useState<OpenSheet>(null);
  useEffect(() => {
    if (!resumeWhenVia || openSheet !== null) return;
    if (whenPrerequisite === resumeWhenVia) {
      setResumeWhenVia(null);
      return;
    }
    setResumeWhenVia(whenPrerequisite);
    setOpenSheet(whenPrerequisite ?? 'when');
  }, [resumeWhenVia, openSheet, whenPrerequisite]);

  const handleChooseAnotherDate = () => {
    setResumeWhenVia(whenPrerequisite);
    setOpenSheet(whenPrerequisite ?? 'when');
  };

  return (
    <>
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between px-4 py-2">
        <button
          type="button"
          aria-label="Go back"
          className="flex size-11 cursor-pointer items-center justify-center"
          onClick={onBack}
        >
          <Icon
            name="long-arrow-up-left"
            width={24}
            height={24}
            strokeWidth={2}
            className="text-jncquoi-neutral-1000"
          />
        </button>
        <ProgressDots step={step} />
      </header>

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pt-6 pb-24">
        {titleHtml && <div className="book-intro-title">{parse(titleHtml)}</div>}

        {/* Form rows */}
        <div className="flex flex-col gap-6">
          {formItems.map((formItem, index) => {
            const step = formItem?.stepPicker?.items?.[0];
            const stepTitle = formItem?.title;
            const stepType = step?.content?.contentType;
            switch (stepType) {
              case EventBookFlowWhichElementModelContentType.eventBookFlowWhich:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.Which')}
                    value={whichDisplay}
                    isEmpty={!booking.event}
                    disabled={isFieldDisabled(stepType)}
                    onClick={() => setOpenSheet('which')}
                  />
                );
              case EventBookFlowWhereElementModelContentType.eventBookFlowWhere:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.Where')}
                    value={whereDisplay}
                    isEmpty={whereIsEmpty}
                    disabled={isFieldDisabled(stepType)}
                    onClick={() => setOpenSheet('where')}
                  />
                );
              case EventBookFlowHowManyElementModelContentType.eventBookFlowHowMany:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.HowMany')}
                    value={guestDisplay}
                    isEmpty={booking.guestCount === null}
                    disabled={isFieldDisabled(stepType)}
                    onClick={() => setOpenSheet('howmany')}
                  />
                );
              case EventBookFlowWhenElementModelContentType.eventBookFlowWhen:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.When')}
                    value={whenDisplay}
                    isEmpty={!booking.date}
                    disabled={isFieldDisabled(stepType)}
                    onClick={() => setOpenSheet('when')}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </div>

      {/* Sticky continue button */}
      <div className="fixed bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <Button
          variant="primary-solid"
          palette="dark"
          size="lg"
          className="w-full"
          disabled={!canContinue}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </div>

      {/* Sheets */}

      {unavailableDate && (
        <DateUnavailableSheet
          open={isUnavailableNoticeOpen}
          onOpenChange={setIsUnavailableNoticeOpen}
          // In the app's language — it sits inside a translated sentence.
          dateLabel={formatDate(unavailableDate.toISOString(), locale)}
          onChooseAnotherDate={handleChooseAnotherDate}
        />
      )}

      {formItems.map((formItem, index) => {
        const step = formItem?.stepPicker?.items?.[0];
        const stepProps = step?.content?.properties;
        const stepType = step?.content?.contentType;
        switch (stepType) {
          case EventBookFlowWhichElementModelContentType.eventBookFlowWhich:
            return (
              <WhichSheet
                key={index}
                isOpen={openSheet === 'which'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as EventBookFlowWhichPropertiesModel}
                booking={booking}
                onBookingChange={onBookingChange}
              />
            );
          case EventBookFlowWhereElementModelContentType.eventBookFlowWhere:
            return (
              <EventWhereSheet
                key={index}
                isOpen={openSheet === 'where'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as EventBookFlowWherePropertiesModel}
                booking={booking}
                onBookingChange={onBookingChange}
                options={whereOptions}
                isLoadingOptions={isLoadingGroupEvents}
              />
            );
          case EventBookFlowHowManyElementModelContentType.eventBookFlowHowMany:
            return (
              <HowManySheet
                key={index}
                isOpen={openSheet === 'howmany'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as EatAndDrinkBookFlowHowManyPropertiesModel}
                booking={booking}
                onBookingChange={onBookingChange}
                infoMessage={
                  guestLimit ? t('App.Labels.EventGuestLimitInfo', { count: guestLimit }) : undefined
                }
              />
            );
          case EventBookFlowWhenElementModelContentType.eventBookFlowWhen:
            return (
              <WhenSheet
                key={index}
                isOpen={openSheet === 'when'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as EatAndDrinkBookFlowWhenPropertiesModel}
                booking={booking}
                onBookingChange={onBookingChange}
                onRequestSuccess={onRequestSuccess}
                enabledDates={enabledDates}
                dateOnly={offersDatePicker && !usesRoomTimePicker}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}
